import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Mail, MapPin, Clock, Send, Building2, Users, Briefcase, HelpCircle, Loader2, CheckCircle, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

import "../styles/marketing.css";
import Seo from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Reveal from "../components/marketing/Reveal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const departments = [
  { value: "sales", label: "Sales & Pricing" },
  { value: "support", label: "Technical Support" },
  { value: "billing", label: "Billing & Accounts" },
  { value: "partnerships", label: "Partnerships" },
  { value: "other", label: "Other" },
];

const contactInfo = [
  { icon: Mail, title: "Email", value: "info@fieldzai.com", description: "Send us an email anytime", href: "mailto:info@fieldzai.com" },
  { icon: MapPin, title: "Office", value: "Sacramento, CA", description: "4190 E Commerce Way, Sacramento, CA 95834", href: "https://maps.google.com/?q=4190+E+Commerce+Way+Sacramento+CA+95834" },
  { icon: Clock, title: "Response Time", value: "< 24 hours", description: "We respond quickly" },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) { toast.error("Please fill in all required fields"); return; }
    if (!formData.subject) { toast.error("Please select a subject/department"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      if (res.data.success) { setSubmitted(true); toast.success(res.data.message || "Message sent!"); }
      else toast.error("Failed to send message.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send message."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="contact-page-root">
      <Seo
        title="Contact Us | Fieldz AI"
        description="Get in touch with the Fieldz AI team for sales, support, billing, or partnership inquiries. We respond within 24 hours."
        path="/contact"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5">Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            Contact us
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
            Have questions about Fieldz AI? We're here to help. Reach out and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Info cards */}
      <section className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-4">
          {contactInfo.map((info, i) => {
            const Tag = info.href ? "a" : "div";
            return (
              <Reveal key={info.title} delay={Math.min(i + 1, 3)}>
                <Tag
                  {...(info.href ? { href: info.href, target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="block h-full text-center rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)]"
                  data-testid={`contact-info-${info.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-11 h-11 rounded-full bg-[var(--mk-blue-light)] flex items-center justify-center mx-auto mb-3">
                    <info.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                  </div>
                  <h3 className="text-[14px] font-semibold text-slate-900 mb-1">{info.title}</h3>
                  <p className="text-[15px] font-semibold text-[var(--mk-blue)]">{info.value}</p>
                  <p className="text-[12.5px] text-slate-500 mt-1">{info.description}</p>
                </Tag>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_10px_30px_-3px_rgba(15,23,42,0.06)]">
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Send us a message</h2>
                <p className="text-[14px] text-slate-500 mb-6">Fill out the form and we'll respond within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Message sent!</h3>
                    <p className="text-[14px] text-slate-500 mb-6">Thank you for reaching out. Our team will get back to you within 24 hours.</p>
                    <button
                      className="mk-btn mk-btn-outline"
                      onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", company: "", subject: "", message: "" }); }}
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="mk-label">Full Name *</label>
                        <input name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" className="mk-input" required data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="mk-label">Email Address *</label>
                        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className="mk-input" required data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="mk-label">Phone Number</label>
                        <input name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="mk-input" data-testid="contact-phone" />
                      </div>
                      <div>
                        <label className="mk-label">Company Name</label>
                        <input name="company" value={formData.company} onChange={handleChange} placeholder="Your Company" className="mk-input" data-testid="contact-company" />
                      </div>
                    </div>
                    <div>
                      <label className="mk-label">Subject / Department *</label>
                      <select name="subject" value={formData.subject} onChange={handleChange} className="mk-select" data-testid="contact-subject" required>
                        <option value="" disabled>Select a topic...</option>
                        {departments.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mk-label">Message *</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} placeholder="How can we help you?" className="mk-textarea" rows={6} required data-testid="contact-message" />
                    </div>
                    <button type="submit" className="mk-btn mk-btn-primary mk-btn-lg w-full" disabled={submitting} data-testid="contact-submit">
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Message</>}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-2 space-y-5">
            <Reveal delay={1}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">Quick Links</h3>
                <div className="space-y-1.5">
                  {[
                    { to: "/support", icon: HelpCircle, label: "Support Center", desc: "Browse FAQs and guides" },
                    { to: "/register", icon: Users, label: "Start Free Trial", desc: "Try Fieldz AI free for 7 days" },
                    { to: "/pricing", icon: Briefcase, label: "View Pricing", desc: "Compare plans and features" },
                  ].map((l) => (
                    <Link key={l.to} to={l.to} className="flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-slate-50 group">
                      <div className="w-9 h-9 rounded-lg bg-[var(--mk-blue-light)] flex items-center justify-center shrink-0">
                        <l.icon className="w-4 h-4 text-[var(--mk-blue)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-slate-900">{l.label}</p>
                        <p className="text-[12.5px] text-slate-500">{l.desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={2}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">Business Hours</h3>
                <div className="space-y-2.5 text-[14px]">
                  {[
                    { day: "Monday – Friday", time: "8:00 AM – 6:00 PM" },
                    { day: "Saturday", time: "9:00 AM – 2:00 PM" },
                    { day: "Sunday", time: "Closed" },
                  ].map((r) => (
                    <div key={r.day} className="flex justify-between">
                      <span className="text-slate-600">{r.day}</span>
                      <span className={`font-semibold ${r.time === "Closed" ? "text-slate-400" : "text-slate-900"}`}>{r.time}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-3 border-t border-slate-100">
                    <p className="text-[12.5px] text-slate-500 leading-relaxed">
                      All times Pacific (PT). For urgent matters:{" "}
                      <a href="mailto:info@fieldzai.com" className="text-[var(--mk-blue)] font-semibold hover:underline">info@fieldzai.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={3}>
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white p-6 shadow-[0_10px_30px_-8px_rgba(0,70,190,0.15)]">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--mk-blue-light)] text-[var(--mk-blue)] text-[11px] font-bold uppercase tracking-wide mb-3">
                  <Building2 className="w-3 h-3" /> Enterprise
                </span>
                <h3 className="text-[16px] font-semibold text-slate-900 mb-2">Need a custom solution?</h3>
                <p className="text-[13.5px] text-slate-600 leading-relaxed mb-4">
                  For enterprise deployments, custom integrations, or high-volume needs, talk to our sales team.
                </p>
                <a href="mailto:info@fieldzai.com?subject=Enterprise Inquiry" className="mk-btn mk-btn-primary w-full" data-testid="enterprise-btn">
                  <Mail className="w-4 h-4" /> Contact Sales
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden h-[280px] border border-slate-200 shadow-sm">
            <iframe
              title="Fieldz AI Office"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3118.3!2d-121.507!3d38.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ad0e6f4c0c0c1%3A0x0!2s4190+E+Commerce+Way%2C+Sacramento%2C+CA+95834!5e0!3m2!1sen!2sus!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ContactPage;
